import React, { FC, useCallback, useState, useMemo } from 'react';
import { Select } from 'antd';
import { useCachedData } from '../../../connection/CacheContext';

type Props = {
  value?: string[]; // array of recruiter IDs
  onChange?: (value: string[]) => void;
  size?: any;
  placeholder?: string;
};

export const RestrictedCompanies: FC<Props> = ({ value = [], onChange, size, placeholder }) => {
  const { recruiterData } = useCachedData();
  const [search, setSearch] = useState('');

  // Build available options (filter by search, limit to 7)
  const options = useMemo(() => {
    if (!Array.isArray(recruiterData)) return [];

    const searchLower = search.toLowerCase().trim();

    const data = recruiterData as any[];
    const filtered = data.filter((recruiter: any) => {
      const name = recruiter?.name;
      return typeof name === 'string' && name.toLowerCase().includes(searchLower);
    });

    return filtered.slice(0, 7).map((recruiter: any) => ({
      id: recruiter.recruiterId ?? recruiter.id ?? String(recruiter),
      value: recruiter.name ?? String(recruiter),
    }));
  }, [recruiterData, search]);

  // Map ID → Label
  const idToLabel = useCallback(
    (id: string) => {
      const found = (recruiterData as any[])?.find(
        (recruiter: any) => recruiter.recruiterId === id || recruiter.id === id,
      );
      return found && typeof found.name === 'string' ? found.name : id;
    },
    [recruiterData],
  );

  const handleChange = useCallback(
    (selectedLabels: string[]) => {
      const data = recruiterData as any[];
      const selectedIds = selectedLabels
        .map((label) => {
          const match = data?.find(
            (recruiter: any) =>
              (recruiter?.name ?? '').trim().toLowerCase() === label.trim().toLowerCase(),
          );
          return match ? match.recruiterId ?? match.id ?? label : label; // fallback for manual entry
        })
        .filter(Boolean) as string[];
      onChange?.(selectedIds);
    },
    [onChange, recruiterData],
  );

  const handleSearch = useCallback((text: string) => {
    setSearch(text);
  }, []);

  const displayValues = value.map(idToLabel);

  return (
    <Select
      mode="tags"
      showSearch
      size={size}
      placeholder={placeholder}
      value={displayValues}
      onSearch={handleSearch}
      onChange={handleChange}
      style={{ width: '100%' }}
      filterOption={false} // use our custom filtering
    >
      {options.map((item) => (
        <Select.Option key={item.id} value={item.value}>
          {item.value}
        </Select.Option>
      ))}
    </Select>
  );
};
